/**
 * 相机装置（Frostpunk 风格俯视城建镜头）—— 从 main.ts 逐字迁出。
 *
 * 拆分第二刀。边界的划法：**这个模块知道怎么移动一台相机，不知道建造模式或
 * 信息面板是什么**。因此键盘策略（Esc 退出放置 / 关闭详情 / 回中，R 旋转预览）
 * 留在 main.ts —— 那是应用级输入策略，不是相机的职责。
 *
 * 迁移中修掉一处**潜伏的 TDZ**：`groundPoint` 原本使用在 1700 行之后才声明的
 * `ray` / `ptr`，只因为它从不在模块初始化时被调用才没崩。现在装置自带这两个
 * 对象，隐患随之消失 —— 这正是拆分要解决的那类问题。
 *
 * 行为安全网：e2e「镜头：右键旋转不穿地、滚轮缩放受约束、静止不漂移」。
 */
import * as THREE from 'three';
import { craterHeight } from './terrain-profile';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = any;


/** 主相机。归装置所有 —— 移动它的逻辑全在本文件里。 */
const camera = new THREE.PerspectiveCamera(44, innerWidth / innerHeight, .5, 11000);
export { camera };


/* ===== Frostpunk 风格镜头：俯视城建 =====
   焦点永远在地面 (y≈0) 上，相机 = 焦点 + 由 yaw/pitch/dist 决定的偏移。
   - WASD / 方向键：平移（带惯性）
   - 滚轮：缩放（拉近拉远，有重量感）
   - 中键 或 右键 拖拽：旋转(偏航)+俯仰
   - 屏幕边缘平移：开
   - Esc / 空格：回到中心俯瞰
*/
const camCtl={
  focus:new THREE.Vector3(0,0,0),   // 看向的地面点
  yaw:.3,                            // 偏航（绕竖直轴）
  pitch:.98,                         // 俯角（越大越俯视），弧度
  dist:640,                          // 相机到焦点距离
  // 目标值（平滑逼近）
  tFocus:new THREE.Vector3(0,0,0),
  tYaw:.3,tPitch:.98,tDist:640,
  vel:new THREE.Vector3(),           // 平移速度（惯性）
  keys:{},
  edgePan:true,
  // 约束
  minPitch:.62,maxPitch:1.35,        // ~35°~77°，保持俯瞰、不翻转
  minDist:14,maxDist:1500,        // 14：建筑本体 10~20m，原值 60 导致永远拉不到可看清单体的距离
  panSpeed:1,
  // 分手感平滑系数（指数逼近速率；越大越跟手）
  panK:7,                             // 平移：稳而不拖
  rotK:14,                            // 旋转：快速跟手
  zoomK:6.5,                          // 缩放：缓而有重量感
  home:{dist:640,yaw:.3,pitch:.98},   // 回中默认位
};


// 键盘
function resetCam(){camCtl.tFocus.set(0,0,0);camCtl.tYaw=camCtl.home.yaw;camCtl.tPitch=camCtl.home.pitch;camCtl.tDist=camCtl.home.dist;}

/**
 * 立即归位（不缓动）—— 换局时用。
 *
 * `resetCam()` 只设目标值，靠 camUpdate 缓动逼近；换局时那会表现为
 * 「从上一局的位置飞过来」。更要紧的是**它必须被调用**：本模块是模块级
 * 单例，`teardownScene()` 清的是场景对象，相机状态原封不动地跨局残留。
 *
 * 实测症状：把镜头平移到坑外 → 返回主菜单 → 进入示范殖民地 →
 * `camCtl.focus` 仍是 (900,0,-900)，屏幕一片漆黑（夜间的坑外空地），
 * 玩家会以为游戏没加载出来。
 *
 * `home` 只存 dist/yaw/pitch 不含 focus，是因为焦点的归位目标恒为原点
 * （陨核井所在），不需要记忆。
 */
function resetCamNow(){
  resetCam();
  camCtl.focus.set(0,0,0);
  camCtl.yaw=camCtl.home.yaw; camCtl.pitch=camCtl.home.pitch; camCtl.dist=camCtl.home.dist;
  camCtl.vel.set(0,0,0);      // 清掉平移惯性，否则归位后还会继续漂
}
// 平滑聚焦到某个地面点（选中建筑/小地图点击用）
function focusOn(x:number,z:number,dist?:number){
  camCtl.tFocus.set(x,0,z);
  if(dist)camCtl.tDist=THREE.MathUtils.clamp(dist,camCtl.minDist,camCtl.maxDist);
}

/** canvas 由 initCameraRig 注入 —— 模块本身拿不到 renderer。 */
let canvas: HTMLCanvasElement;

/** 装置自带的射线与指针 —— 不再依赖 main.ts 里靠后声明的同名变量。 */
const ray = new THREE.Raycaster(), ptr = new THREE.Vector2();

// 失焦/隐藏时清空按键并停边缘平移，避免镜头被"卡住"的方向持续推走
addEventListener('blur',()=>{camCtl.keys={};});
document.addEventListener('visibilitychange',()=>{if(document.hidden)camCtl.keys={};});


/* 地形拾取：解析法光线步进，不再对 8.5 万三角形做暴力相交。
   craterHeight 是纯函数，沿射线找「射线高度 - 地形高度」的变号点再二分，
   ~60 次三角函数就出结果 —— 这条路径在滚轮缩放、平移、建造预览、
   铺路里每次指针移动都会走，是交互手感的关键热路径。 */
const _tpV=new THREE.Vector3();
function terrainPick(rc,out){
  const o=rc.ray.origin,d=rc.ray.direction;
  const f=(t)=>o.y+d.y*t-craterHeight(o.x+d.x*t,o.z+d.z*t);
  let t0=0;
  if(f(0)<0)return false;                  // 起点已在地下（不应发生）
  for(let t=60;t<=9000;t+=60){             // 60m 步进找变号区间
    if(f(t)<=0){                           // 在 (t0,t] 之间穿过地面，二分收敛
      let a=t0,b=t;
      for(let i=0;i<24;i++){const m=(a+b)/2;(f(m)>0)?(a=m):(b=m);}
      const tm=(a+b)/2;
      out.set(o.x+d.x*tm,o.y+d.y*tm,o.z+d.z*tm);
      return true;
    }
    t0=t;
  }
  return false;
}
// 地面拾取：指针 → 地面点（缩放锚定 / 平移映射共用）
function groundPoint(cx,cy,out){
  ptr.set(cx/innerWidth*2-1,-(cy/innerHeight)*2+1);
  ray.setFromCamera(ptr,camera);
  return terrainPick(ray,out);
}

// 滚轮缩放（重量感 + 朝光标锚定：光标下的地面点在缩放前后保持不动）
addEventListener('wheel',e=>{
  const t=e.target as HTMLElement|null;
  if(t&&typeof t.closest==='function'&&t.closest('.panel,.buildmenu,.hud,#binfo'))return;
  const f=Math.exp(e.deltaY*0.0012);
  const nd=THREE.MathUtils.clamp(camCtl.tDist*f,camCtl.minDist,camCtl.maxDist);
  const anchor=new THREE.Vector3();
  if(groundPoint(e.clientX,e.clientY,anchor)){
    // 解析式锚定：缩放是把相机沿"焦点→光标地面点"方向拉近/拉远，
    // 焦点应向锚点方向平移 dist 变化量的水平分量，使光标处地面点保持静止。
    const ratio=1-nd/camCtl.tDist;           // 拉近(nd<旧)为负 → 焦点移向锚点
    camCtl.tFocus.x+=(anchor.x-camCtl.tFocus.x)*ratio;
    camCtl.tFocus.z+=(anchor.z-camCtl.tFocus.z)*ratio;
    camCtl.tFocus.x=THREE.MathUtils.clamp(camCtl.tFocus.x,-900,900);
    camCtl.tFocus.z=THREE.MathUtils.clamp(camCtl.tFocus.z,-900,900);
  }
  camCtl.tDist=nd;
  e.preventDefault();
},{passive:false});

// 鼠标旋转 / 平移
let dragBtn=-1,lastMX=0,lastMY=0;
addEventListener('pointerup',()=>{dragBtn=-1;});
addEventListener('pointermove',e=>{
  if(window.__placingMode)return;             // 放置模式下镜头不抢鼠标
  const dx=e.clientX-lastMX,dy=e.clientY-lastMY;lastMX=e.clientX;lastMY=e.clientY;
  if(dragBtn===2||dragBtn===1){           // 右键/中键：旋转+俯仰
    camCtl.tYaw-=dx*0.005;
    camCtl.tPitch=THREE.MathUtils.clamp(camCtl.tPitch+dy*0.004,camCtl.minPitch,camCtl.maxPitch);
  }else if(dragBtn===0&&e.shiftKey){      // Shift+左键：平移
    panByScreen(dx,dy);
  }
});

// 把屏幕像素位移转成地面平移（沿 yaw 朝向）
function panByScreen(dx,dy){
  const s=camCtl.dist*0.0016*camCtl.panSpeed;
  const fx=-Math.sin(camCtl.tYaw),fz=-Math.cos(camCtl.tYaw);   // 前向
  const rx=Math.cos(camCtl.tYaw),  rz=-Math.sin(camCtl.tYaw);   // 右向
  camCtl.tFocus.x+=(-dx*rx+dy*fx)*s;
  camCtl.tFocus.z+=(-dx*rz+dy*fz)*s;
}

// 屏幕边缘平移
let mouseX=innerWidth/2,mouseY=innerHeight/2,mouseIn=true;
addEventListener('pointermove',e=>{mouseX=e.clientX;mouseY=e.clientY;});

function camUpdate(dt){
  // WASD 平移 → 目标焦点
  const sp=camCtl.dist*0.9*dt;   // 速度随距离缩放，远处移得快
  let mx=0,mz=0;
  if(camCtl.keys['KeyW']||camCtl.keys['ArrowUp'])mz+=1;
  if(camCtl.keys['KeyS']||camCtl.keys['ArrowDown'])mz-=1;
  if(camCtl.keys['KeyA']||camCtl.keys['ArrowLeft'])mx-=1;
  if(camCtl.keys['KeyD']||camCtl.keys['ArrowRight'])mx+=1;
  // Q/E 旋转
  if(camCtl.keys['KeyQ'])camCtl.tYaw+=1.6*dt;
  if(camCtl.keys['KeyE'])camCtl.tYaw-=1.6*dt;
  // 屏幕边缘平移：仅文档聚焦且鼠标在画布内才生效，距边缘越近越快（中心平滑过渡）
  if(camCtl.edgePan&&mouseIn&&dragBtn<0&&document.hasFocus()&&!document.hidden){
    const M=28;
    let ex=0,ez=0;
    if(mouseX<M)ex=-(M-mouseX)/M; else if(mouseX>innerWidth-M)ex=(mouseX-(innerWidth-M))/M;
    if(mouseY<M)ez=-(M-mouseY)/M; else if(mouseY>innerHeight-M)ez=(mouseY-(innerHeight-M))/M;
    mx+=ex; mz+=ez;
  }
  if(mx||mz){
    const fx=-Math.sin(camCtl.tYaw),fz=-Math.cos(camCtl.tYaw);
    const rx=Math.cos(camCtl.tYaw),  rz=-Math.sin(camCtl.tYaw);
    camCtl.tFocus.x+=(mx*rx+mz*fx)*sp;
    camCtl.tFocus.z+=(mx*rz+mz*fz)*sp;
  }
  // 城市范围内软约束
  camCtl.tFocus.x=THREE.MathUtils.clamp(camCtl.tFocus.x,-900,900);
  camCtl.tFocus.z=THREE.MathUtils.clamp(camCtl.tFocus.z,-900,900);

  // 平滑逼近（分手感：平移稳、旋转快、缩放缓），帧率无关
  const kP=1-Math.exp(-camCtl.panK*dt);
  const kR=1-Math.exp(-camCtl.rotK*dt);
  const kZ=1-Math.exp(-camCtl.zoomK*dt);
  camCtl.focus.lerp(camCtl.tFocus,kP);
  camCtl.yaw+=(camCtl.tYaw-camCtl.yaw)*kR;
  camCtl.pitch+=(camCtl.tPitch-camCtl.pitch)*kR;
  camCtl.dist+=(camCtl.tDist-camCtl.dist)*kZ;

  // 由 球坐标 摆相机
  const cp=Math.cos(camCtl.pitch),spch=Math.sin(camCtl.pitch);
  const ox=Math.sin(camCtl.yaw)*cp, oz=Math.cos(camCtl.yaw)*cp;
  camera.position.set(
    camCtl.focus.x+ox*camCtl.dist,
    camCtl.focus.y+spch*camCtl.dist,
    camCtl.focus.z+oz*camCtl.dist);
  camera.lookAt(camCtl.focus.x,0,camCtl.focus.z);
}
// 初始位
camUpdate(0.016);

/** 地形拾取的复用向量。调用方约定：terrainPick/groundPoint 写入它，随后立即读取。 */
export { camCtl, resetCam, resetCamNow, focusOn, terrainPick, groundPoint, camUpdate, _tpV };

/** 注册指针/滚轮/边缘平移监听。必须在 canvas 就绪后调用一次。 */
export function initCameraRig(canvasEl: HTMLCanvasElement): void {
  canvas = canvasEl;
  /* 这几个监听器绑在 canvas 上，只能等它就绪后再注册；
     其余（pointerup / pointermove / wheel / blur）绑在 window，模块加载时已就位。 */
  canvas.addEventListener('pointerdown', (e) => {
    if (window.__placingMode) return;           // 放置模式下镜头不抢鼠标
    dragBtn = e.button; lastMX = e.clientX; lastMY = e.clientY;
    // 合成事件/边界情况下没有活动指针，捕获会抛 —— 忽略即可
    try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  });

  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  canvas.addEventListener('pointerenter', () => { mouseIn = true; });
  canvas.addEventListener('pointerleave', () => { mouseIn = false; });
  camUpdate(0.016);
}
